import { Link } from "react-router-dom";
import { Copy, Check, CaretRight, ArrowCounterClockwise } from "@phosphor-icons/react";
import { STATUS_LABELS, fmtInt, devCountdown } from "@/lib/api";
import { parsePedidoNaLoja, orderSkus } from "@/lib/orderUtils";
import MarketplaceBadge from "@/components/orders/MarketplaceBadge";

/**
 * Card mobile de pedido — usado em telas < md.
 * Mantém as mesmas informações da linha da tabela mas em layout empilhado,
 * otimizado para leitura no dedo (sem scroll horizontal).
 */
export default function OrderCardMobile({
  order,
  selected,
  canAdmin,
  onToggleSelect,
  onOpen,
  copiedCode,
  onCopyShopCode,
  showDeleted,
  onRestore,
  restoring,
}) {
  const shop = parsePedidoNaLoja(order);
  const dev = devCountdown(order);

  return (
    <div
      className={`border-2 bg-white p-4 transition-colors ${
        selected ? "border-black bg-yellow-50" : "border-zinc-200"
      }`}
      data-testid={`order-card-${order.bling_number}`}
    >
      {/* Linha 1: checkbox + número + badges */}
      <div className="flex items-start gap-3 mb-3">
        {canAdmin && !showDeleted && (
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0"
            checked={selected}
            onChange={(e) => {
              e.stopPropagation();
              onToggleSelect(order.id);
            }}
            data-testid={`card-select-order-${order.bling_number}`}
            aria-label={`Selecionar pedido ${order.bling_number}`}
          />
        )}
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => onOpen(order.id)}
            className="font-mono font-black text-lg tracking-tight text-left"
            data-testid={`card-order-open-${order.bling_number}`}
          >
            #{order.bling_number}
          </button>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            {order.marketplace_source && (
              <MarketplaceBadge marketplace={order.marketplace_source} />
            )}
            {order.store_name && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                {order.store_name}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onOpen(order.id)}
          className="shrink-0 text-zinc-400"
          aria-label="Abrir pedido"
        >
          <CaretRight size={20} weight="bold" />
        </button>
      </div>

      {/* Cliente */}
      <div className="mb-3">
        <div className="overline text-[10px] text-zinc-400 mb-0.5">CLIENTE</div>
        <div className="font-semibold text-sm leading-tight">
          {order.customer || "—"}
        </div>
      </div>

      {/* SKUs */}
      {orderSkus(order).length > 0 && (
        <div className="mb-3" data-testid={`card-skus-${order.bling_number}`}>
          <div className="overline text-[10px] text-zinc-400 mb-0.5">SKUS</div>
          <div className="flex flex-wrap gap-1">
            {orderSkus(order).slice(0, 4).map((s) => (
              <span key={s} className="font-mono text-[10px] border border-zinc-300 bg-zinc-50 px-1.5 py-0.5">{s}</span>
            ))}
            {orderSkus(order).length > 4 && (
              <span className="text-[10px] text-zinc-400 font-mono">+{orderSkus(order).length - 4}</span>
            )}
          </div>
        </div>
      )}

      {/* Pedido na loja + Marketplace */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="overline text-[10px] text-zinc-400 mb-0.5">PEDIDO NA LOJA</div>
          {shop.code ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyShopCode(shop.code);
                }}
                className="shrink-0 inline-flex items-center justify-center w-6 h-6 border border-zinc-300 hover:border-black hover:bg-black hover:text-white rounded transition-colors"
                title={`Copiar ${shop.code}`}
                data-testid={`card-copy-shop-code-${order.bling_number}`}
              >
                {copiedCode === shop.code ? (
                  <Check size={12} weight="bold" />
                ) : (
                  <Copy size={12} weight="bold" />
                )}
              </button>
              <span className="font-mono text-xs truncate">{shop.code}</span>
            </div>
          ) : (
            <span className="text-zinc-300 text-xs">—</span>
          )}
        </div>
        <div>
          <div className="overline text-[10px] text-zinc-400 mb-0.5">MARKETPLACE</div>
          <div className="text-xs text-zinc-700 truncate">
            {order.marketplace || "—"}
          </div>
        </div>
      </div>

      {/* Categoria */}
      {order.category && (
        <div className="mb-3">
          <div className="overline text-[10px] text-zinc-400 mb-0.5">CATEGORIA</div>
          <div className="text-xs text-zinc-700">{order.category}</div>
        </div>
      )}

      {/* Status + dev countdown */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className={`dot ${order.production_status}`} />
        <span className="text-sm font-medium">
          {STATUS_LABELS[order.production_status]}
        </span>
        {showDeleted && (
          <span className="text-[10px] font-bold uppercase tracking-widest bg-zinc-800 text-white px-2 py-0.5" data-testid={`card-deleted-badge-${order.bling_number}`}>
            EXCLUÍDO
          </span>
        )}
        {dev.applies && dev.days_remaining > 0 && (
          <span
            className="text-[10px] font-bold uppercase tracking-widest bg-black text-white px-2 py-0.5"
            data-testid={`card-dev-countdown-${order.bling_number}`}
          >
            {dev.days_remaining}d dev
          </span>
        )}
        {dev.applies && dev.days_remaining <= 0 && (
          <span
            className="text-[10px] font-bold uppercase tracking-widest bg-[#E60000] text-white px-2 py-0.5"
            data-testid={`card-dev-overdue-${order.bling_number}`}
          >
            DEV ATRASADO {dev.days_overdue}D
          </span>
        )}
      </div>

      {/* Rodapé: quantidade + botão abrir */}
      <div className="flex items-center justify-between pt-3 border-t border-zinc-200">
        <div>
          <span className="font-mono font-black text-lg">
            {fmtInt(order.total_cups || 0)}
          </span>
          <span className="text-xs text-zinc-500 ml-1.5">copo(s)</span>
        </div>
        <div className="flex items-center gap-3">
          {showDeleted && (
            <button
              type="button"
              className="text-[10px] font-bold uppercase tracking-widest border border-black bg-black text-white px-2.5 py-1.5 disabled:opacity-50"
              onClick={() => onRestore(order.id)}
              disabled={restoring}
              data-testid={`card-restore-order-${order.bling_number}`}
            >
              <ArrowCounterClockwise size={11} weight="bold" className="inline mr-1" />
              {restoring ? "Restaurando..." : "Restaurar"}
            </button>
          )}
          <Link
            to={`/orders/${order.id}`}
            className="text-xs font-bold uppercase tracking-widest underline"
            data-testid={`card-order-link-${order.bling_number}`}
          >
            Abrir <CaretRight size={12} weight="bold" className="inline" />
          </Link>
        </div>
      </div>
    </div>
  );
}
